import * as React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useOAuthProviders } from '@/hooks/useOAuthProviders'
import { oauthService } from '@/services/oauthService'
import { toast } from '@/toast/toastStore'
import { Button } from '@/components/ui/Button'
import { AuthShell } from '@/components/auth/AuthShell'
import { FormField, OrDivider } from '@/components/auth/FormField'
import { SocialLoginButtonsV2 } from '@/components/auth/SocialLoginButtonsV2'
import type { LoginRequest, OAuthProvider } from '@/types'

/**
 * returnTo パラメータのオープンリダイレクト防止バリデーション。
 * `/` で始まる相対パスのみを許可し、`//` や `http(s)://` 等の外部 URL は拒否する。
 * 安全な場合はそのままのパスを返し、不正な場合は null を返す。
 */
function safeReturnTo(value: string | null): string | null {
  if (!value) return null
  // 相対パス: `/` で始まり、`//`（プロトコル相対URL）は拒否
  if (value.startsWith('/') && !value.startsWith('//')) return value
  return null
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldErrors {
  email?: string
  password?: string
}

/**
 * Sottovo ログインページ (`/auth/login`)。
 *
 * 既存認証ロジックをそのまま再利用する:
 *   - useAuth().login(credentials: LoginRequest): Promise<boolean>
 *   - 成功時 → navigate('/dashboard')
 *   - oauthService.startOAuthFlow(provider) で OAuth フロー開始
 *
 * UI のみ v2 ブランド (静か・控えめ) で再構築。エラー通知 (antd message) は
 * useAuth フック内部で発火するため、ここではクライアント側バリデーションのみ表示する。
 */
export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const { providers: oauthProviders, isLoading: oauthProvidersLoading } = useOAuthProviders()

  // returnTo: オープンリダイレクト防止済みの遷移先（null の場合は既定ページへ）
  const returnTo = safeReturnTo(searchParams.get('returnTo'))

  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [loading, setLoading] = React.useState(false)
  const [oauthLoading, setOAuthLoading] = React.useState(false)
  const [oauthProvider, setOAuthProvider] = React.useState<OAuthProvider | null>(null)

  function validate(): FieldErrors {
    const next: FieldErrors = {}
    if (!email) next.email = 'メールアドレスを入力してください'
    else if (!EMAIL_RE.test(email))
      next.email = '有効なメールアドレスを入力してください'
    if (!password) next.password = 'パスワードを入力してください'
    else if (password.length < 8)
      next.password = 'パスワードは8文字以上で入力してください'
    return next
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const validation = validate()
    setErrors(validation)
    if (Object.keys(validation).length > 0) return

    setLoading(true)
    try {
      const credentials: LoginRequest = { email, password }
      const success = await login(credentials)
      if (success) {
        // pending link があれば complete
        const pendingRaw = sessionStorage.getItem('oauth_pending_link')
        if (pendingRaw) {
          let pendingProvider = ''
          try {
            const pending = JSON.parse(pendingRaw) as { link_intent_token: string; provider: string; email: string }
            pendingProvider = pending.provider
            sessionStorage.removeItem('oauth_pending_link')
            await oauthService.completeLink(pending.link_intent_token)
            toast.success(`${pending.provider}を連携しました`)
          } catch {
            // ログイン自体は成功しているのでダッシュボードへは進むが、
            // 連携失敗（token期限切れ・メール不一致等）はユーザーに明示する
            sessionStorage.removeItem('oauth_pending_link')
            toast.error(
              `${pendingProvider || 'アカウント'}の連携に失敗しました。設定画面から再度お試しください。`
            )
          }
        }
        navigate(returnTo ?? '/dashboard')
      }
    } finally {
      setLoading(false)
    }
  }

  function handleSocialLogin(provider: OAuthProvider) {
    setOAuthLoading(true)
    setOAuthProvider(provider)
    try {
      oauthService.startOAuthFlow(provider)
    } catch (error) {
      console.error('OAuth start error:', error)
      setOAuthLoading(false)
      setOAuthProvider(null)
    }
  }

  return (
    <AuthShell
      title="おかえりなさい"
      subtitle="アシスタントが今日の準備を整えています"
    >
      <SocialLoginButtonsV2
        onLogin={handleSocialLogin}
        loading={oauthLoading}
        loadingProvider={oauthProvider}
        disabled={loading}
        providers={oauthProviders}
        providersLoading={oauthProvidersLoading}
      />

      {(oauthProvidersLoading || oauthProviders.length > 0) && (
        <div className="my-6">
          <OrDivider label="または メールアドレスで続行" />
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <FormField
          id="login-email"
          label="メールアドレス"
          type="email"
          autoComplete="email"
          placeholder="your.email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />

        <FormField
          id="login-password"
          label="パスワード"
          type="password"
          autoComplete="current-password"
          placeholder="パスワードを入力"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />

        <div className="-mt-1 text-right">
          <Link
            to="/auth/forgot-password"
            className="text-sm text-v2-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
          >
            パスワードを忘れた場合
          </Link>
        </div>

        <Button
          type="submit"
          size="md"
          className="w-full"
          disabled={loading || oauthLoading}
        >
          {loading ? 'ログイン中…' : 'ログイン'}
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-v2-ink-muted">
        はじめての方は{' '}
        <Link
          to="/auth/register"
          className="font-medium text-v2-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
        >
          会員登録
        </Link>
      </p>
    </AuthShell>
  )
}
